import { ReactNode, useMemo, useState } from "react";
import { Flex } from "@chakra-ui/react";
import { useOptionalCombineFilters } from "utils/useCombineFilters";
import type { AppliedFilter } from "components/Shared/FilterEditor/utils";
import { useGraphileFiltersUrlQuery } from "components/Shared/FilterEditor/urlQuerySerializers";
import { OrderingPlatformFilter } from "__generated__/gql/graphql";
import FilterBarWithSearch, {
  FilterBarWithSearchProps,
  FreestandingFilterBarContainer,
  FreestandingFilterWell,
} from "components/Shared/FilterEditor/FilterBar/FilterBarWithSearch";
import getFilterDefinitions from "components/Shared/OrderingPlatformDirectory/filterDefinitions";
import getResults from "components/Shared/FilterEditor/getResults";
import { FREESTANDING_TABLE_CONTAINER_PROPS } from "components/Shared/ChakraReactTable/FullPageTable";
import IncludeCustomFilter, {
  INCLUDE_CUSTOM_FILTER_NAME,
  getValueFromFilters as getIncludeCustomValueFromFilters,
  makeFiltersHandleChange as makeFiltersHandleIncludeCustomChange,
} from "components/Shared/IncludeCustomFilter";
import AdminManageOrderingPlatformsTable, {
  AdminManageOrderingPlatformsTableProps,
} from "./Table";

const filterDefinitions = getFilterDefinitions(true);

const DEFAULT_FILTERS: AppliedFilter[] = [
  { filter: INCLUDE_CUSTOM_FILTER_NAME, value: false, variant: "equalTo" },
];

type CommonProps = Omit<AdminManageOrderingPlatformsTableProps, "isReady"> &
  Pick<
    FilterBarWithSearchProps,
    "FilterBarContainerComponent" | "FiltersContainerComponent"
  > & {
    defaultFilters?: AppliedFilter[];
    headerRight?: ReactNode;
  };

type BaseProps = Omit<CommonProps, "defaultFilters"> & {
  filters: AppliedFilter[];
  isTableReady?: boolean;
  onFiltersChange: (filters: AppliedFilter[]) => void;
};

function BaseAdminManageOrderingPlatformsTable({
  FilterBarContainerComponent,
  FiltersContainerComponent,
  filter,
  filters,
  headerRight,
  isTableReady = true,
  onFiltersChange: handleFiltersChange,
  ...rest
}: BaseProps) {
  return (
    <>
      <FilterBarWithSearch
        key={String(isTableReady)}
        filterDefinitions={filterDefinitions}
        filters={filters}
        onFiltersChange={handleFiltersChange}
        headerRight={
          <Flex gap={4}>
            <IncludeCustomFilter
              isChecked={
                isTableReady && getIncludeCustomValueFromFilters(filters)
              }
              onChange={makeFiltersHandleIncludeCustomChange(
                filters,
                handleFiltersChange,
              )}
            />
            {headerRight}
          </Flex>
        }
        {...{ FilterBarContainerComponent, FiltersContainerComponent }}
      />
      <AdminManageOrderingPlatformsTable
        {...rest}
        filter={filter}
        isReady={isTableReady}
      />
    </>
  );
}

export default function AdminManageOrderingPlatformsTableView({
  defaultFilters = DEFAULT_FILTERS,
  filter: filterProp,
  ...rest
}: CommonProps) {
  const { filter, filters, isTableReady, handleFiltersChange } =
    useGraphileFiltersUrlQuery<OrderingPlatformFilter>(filterDefinitions, {
      defaultFilters,
      externalQueryKeys: ["selectedId"],
    });

  const tableFilter = useOptionalCombineFilters(filterProp, filter);

  return (
    <BaseAdminManageOrderingPlatformsTable
      {...{ filters, isTableReady }}
      {...rest}
      filter={tableFilter}
      onFiltersChange={handleFiltersChange}
    />
  );
}

export function EmbeddedAdminManageOrderingPlatformsTable({
  defaultFilters = DEFAULT_FILTERS,
  filter: filterProp,
  ...rest
}: CommonProps) {
  const [filters, setFilters] = useState<AppliedFilter[]>(defaultFilters);

  const filterResults = useMemo(() => {
    if (!filters?.length) return;
    return getResults<OrderingPlatformFilter>(filterDefinitions, filters);
  }, [filters]);

  const tableFilter = useOptionalCombineFilters(filterProp, filterResults);

  return (
    <BaseAdminManageOrderingPlatformsTable
      {...{ filters }}
      {...rest}
      {...FREESTANDING_TABLE_CONTAINER_PROPS}
      filter={tableFilter}
      onFiltersChange={setFilters}
      FilterBarContainerComponent={FreestandingFilterBarContainer}
      FiltersContainerComponent={FreestandingFilterWell}
    />
  );
}
